import _, { startCase } from "lodash";
import { DateTime } from "luxon";
import moment from "moment";
import { Badge } from "@shared/ui";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";

import { ClientContext } from "../App";
import { getIgnitionSessions } from "../lib/automatic";
import seedData from "../seed/ignitionSessions.json";
import LoadingSpinner from "./LoadingSpinner";
import { SelectColumnFilter } from "./table/Filter";
import { Metadata } from "./table/Metadata";
import Table from "./table/Table";
import { User } from "./table/User";

const IGNITION_SESSIONS_AREA = "ignition-sessions-table";

const IgnitionSessionsTable = () => {
  const now = DateTime.local().endOf("day"); // Set to end of day local time to be inclusive in date range

  const { selectedClient: clientId } = useContext(ClientContext);
  const { promiseInProgress } = usePromiseTracker({
    area: IGNITION_SESSIONS_AREA,
  });
  const [ignitionSessions, setIgnitionSessions] = useState([]);

  const [startDate, setStartDate] = useState(
    now.minus({ months: 1 }).startOf("day").toJSDate() // Set to start of day local time to be inclusive in date range
  );
  const [endDate, setEndDate] = useState(now.toJSDate());

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "status",
        Filter: SelectColumnFilter,
        filter: "includes",
        Cell: ({ value }) => {
          return value ? <Badge status={value} /> : "—";
        },
        width: 75,
      },

      {
        id: "user",
        Header: "User",
        accessor: (row) => {
          const user = _(row).get("user");
          return JSON.stringify(user);
        },
        Cell: ({ value }) => {
          return value ? <User value={value} /> : "—";
        },
        width: 200,
        isObject: true,
      },

      {
        Header: "Carrier",
        accessor: "carrier",
        Cell: ({ value }) => {
          return startCase(value) || "—";
        },
        width: 100,
      },

      {
        Header: "Result",
        id: "result",
        accessor: (row) => {
          return row.result ? row.resultLabel ?? "—" : "—";
        },
        Cell: ({ value }) => {
          return value;
        },
        width: 75,
      },

      {
        Header: "Result Detail",
        id: "resultDetail",
        accessor: (row) => {
          return row.resultDetail ? row.resultDetailLabel ?? "—" : "—";
        },
        Cell: ({ value }) => {
          return value;
        },
        width: 125,
      },

      {
        id: "metadata",
        Header: "Metadata",
        accessor: (row) => JSON.stringify(row.metadata),
        Cell: ({ value }) => {
          return value ? <Metadata value={value} /> : "—";
        },
        width: 150,
        isObject: true,
      },

      {
        id: "lastUpdated",
        Header: "Last Updated",
        accessor: (row) => row.modifiedAt || row.createdAt,
        disableFilters: true,
        Cell: ({ value }) => {
          return value
            ? DateTime.fromISO(value).toLocaleString({
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              })
            : "—";
        },
        width: 75,
      },
      {
        accessor: "id",
        show: false,
        className: "hidden",
      },
      {
        accessor: "account",
        show: false,
        className: "hidden",
      },
      {
        accessor: "createdAt",
        show: false,
        className: "hidden",
      },
      {
        accessor: "modifiedAt",
        show: false,
        className: "hidden",
      },
    ],
    []
  );

  useEffect(() => {
    if (process.env.REACT_APP_MOCK === "true") {
      setIgnitionSessions(seedData);
      return;
    }

    if (clientId) {
      // Reset ignition sessions while loading new data
      setIgnitionSessions([]);

      trackPromise(
        getIgnitionSessions(
          {
            start: startDate && startDate.toISOString(),
            end: endDate && moment(endDate).add(1, "day").toISOString(), //We need +1 day to be inclusive in date range. Otherwise, if we try to get the data for the same day, we will get no data
          },
          clientId
        ).then((data) => {
          setIgnitionSessions(data);
        }),
        IGNITION_SESSIONS_AREA
      );
    }
  }, [endDate, clientId, startDate]); // Add startDate as a dependency as well

  return (
    <>
      {promiseInProgress === true ? (
        <LoadingSpinner />
      ) : (
        <>
          <Table
            entity={"ignition-sessions"}
            data={ignitionSessions}
            columns={columns}
            rowBehavior={{
              type: "link",
              config: { path: "/ignition", resourceKey: "id" },
            }}
            globalFilterPlaceholder={
              "Enter any user, status, carrier, or metadata..."
            }
            csvFields={[
              "id",
              "status",
              "carrier",
              "result",
              "resultDetail",
              "account",
              "user",
              "metadata",
              "createdAt",
              "modifiedAt",
            ]}
            dateFilter={{ startDate, endDate, setStartDate, setEndDate }}
          />
        </>
      )}
    </>
  );
};

export default IgnitionSessionsTable;
